import React, { useState, useRef, useContext, useEffect } from "react";
import {
  X,
  ChevronRight,
  Settings,
  CreditCard,
  User,
  Check,
  Edit2,
} from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { SessionContext } from "../context/sessionContext";
import Cookies from "js-cookie";
import { plans, oneTimeStudy, studentPlan } from "../assets/data/pricingData";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useUser } from "../context/UserContext";
import { CameraAlt } from "@mui/icons-material";

const SettingsModal = ({ isOpen, onClose, onLogout }) => {
  const [currentView, setCurrentView] = useState("account");
  const [selectedTheme, setSelectedTheme] = useState("System");
  const { session } = useContext(SessionContext);
  const {
    userData,
    profilePicUrl,
    updateUser,
    roleName,
    getInitials,
    refreshUser,
    isOrgDeactivated,
    orgId,
  } = useUser();
  const [planDetails, setPlanDetails] = useState(null);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivationError, setDeactivationError] = useState("");
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [isRequestingCancelDeactivation, setIsRequestingCancelDeactivation] = useState(false);
  const [requestCancelError, setRequestCancelError] = useState("");
  const [requestCancelSuccess, setRequestCancelSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const NAME_MAX_LENGTH = 40;
  const [nameError, setNameError] = useState("");

  const findPlanByName = (planName) => {
    if (!planName) return null;
    const matchMain = plans.find((p) => p.name === planName);
    if (matchMain) return matchMain;
    if (oneTimeStudy.name === planName) return oneTimeStudy;
    if (studentPlan.name === planName) return studentPlan;
    return null;
  };

  useEffect(() => {
    if (userData) {
      setEditedName(userData.username || "");
      setPlanDetails(findPlanByName(userData.planDetails?.plan_name));
    }
  }, [userData]);

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingPic(true);
    const token = Cookies.get("token");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await fetch(
        `${process.env.REACT_APP_API_URL}/user/uploadProfilePic`,
        {
          method: "POST",
          headers: { token: token },
          body: formData,
          credentials: "include",
        },
      );
      const uploadData = await uploadRes.json();
      if (uploadData.success) {
        const { keys } = uploadData.data;
        const updateRes = await fetch(
          `${process.env.REACT_APP_API_URL}/user/editUser`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", token: token },
            body: JSON.stringify({ first_name: userData.username, profile_pic: keys }),
            credentials: "include",
          },
        );
        const updateData = await updateRes.json();
        if (updateData.success) {
          updateUser({ profile_pic: keys });
        }
      }
    } catch (err) {
      console.error("Error uploading profile picture:", err);
      alert("Failed to upload profile picture");
    } finally {
      setIsUploadingPic(false);
    }
  };

  const handleNameUpdate = async () => {
    if (!editedName.trim() || editedName === userData.username) {
      setIsEditingName(false);
      setEditedName(userData.username);
      return;
    }
    const token = Cookies.get("token");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/editUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: token },
        body: JSON.stringify({ first_name: editedName, profile_pic: userData.profile_pic }),
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        updateUser({ username: editedName });
        setIsEditingName(false);
      } else {
        alert("Failed to update name");
      }
    } catch (err) {
      console.error("Error updating name:", err);
      alert("Failed to update name");
    }
  };

  if (!isOpen) return null;

  const handleChangePassword = async () => {
    const newErrors = {};
    if (!oldPassword) newErrors.oldPassword = "Current password is required";
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8 || newPassword.length > 16) {
      newErrors.newPassword = "Password must be between 8 and 16 characters";
    } else if (!/[a-z]/.test(newPassword)) {
      newErrors.newPassword = "Password must contain at least one lowercase letter";
    } else if (!/[A-Z]/.test(newPassword)) {
      newErrors.newPassword = "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(newPassword)) {
      newErrors.newPassword = "Password must contain at least one digit";
    } else if (!/[!@#$%^&*]/.test(newPassword)) {
      newErrors.newPassword = "Password must contain at least one special character (!@#$%^&*)";
    } else if (oldPassword && newPassword === oldPassword) {
      newErrors.newPassword = "New password must be different from current password";
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const token = Cookies.get("token");
    if (!token) return;
    try {
      setIsUpdatingPassword(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: token },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (data.success || data.status === "success") {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({});
        setPasswordSuccess(true);
        setTimeout(() => {
          setPasswordSuccess(false);
          setCurrentView("account");
        }, 1500);
      } else {
        setErrors({ apiError: data.message || "Failed to update password" });
      }
    } catch {
      setErrors({ apiError: "Something went wrong. Please try again." });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDeactivateAccount = async () => {
    setIsDeactivating(true);
    setDeactivationError("");
    setShowDeactivateDialog(false);
    const token = Cookies.get("token");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/deactivateUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: token },
        body: JSON.stringify({ userId: session?.user?._id, email: session?.user?.email }),
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
        setDeactivationError("");
        onClose();
      } else {
        setDeactivationError(data.message || "Failed to deactivate account");
        setTimeout(() => setDeactivationError(""), 5000);
      }
    } catch (err) {
      console.error("Error deactivating account:", err);
      setDeactivationError("Failed to deactivate account. Please try again.");
      setTimeout(() => setDeactivationError(""), 5000);
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleRequestCancelDeactivation = async () => {
    setIsRequestingCancelDeactivation(true);
    setRequestCancelError("");
    setRequestCancelSuccess(false);
    const token = Cookies.get("token");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/request-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: token },
        body: JSON.stringify({ orgId: orgId }),
      });
      const data = await res.json();
      if (data.success) {
        setRequestCancelSuccess(true);
        setTimeout(() => setRequestCancelSuccess(false), 4000);
      } else {
        setRequestCancelError(data.message || "Failed to send request");
        setTimeout(() => setRequestCancelError(""), 5000);
      }
    } catch (err) {
      console.error("Error requesting cancel deactivation:", err);
      setRequestCancelError("Failed to send request. Please try again.");
      setTimeout(() => setRequestCancelError(""), 5000);
    } finally {
      setIsRequestingCancelDeactivation(false);
    }
  };

  const handleLogoutClick = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      onClose();
      onLogout();
    }
  };

  const name = userData?.username || userData.displayName;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const renderSidebar = (activeView) => (
    <div className="settings-modal-sidebar">
      <button onClick={onClose} className="settings-modal-close">
        <X size={24} />
      </button>
      <button
        onClick={() => setCurrentView("account")}
        className={`settings-modal-menu-item ${activeView === "account" ? "active" : ""}`}
      >
        <User size={20} />
        <span className="font-medium">Account</span>
      </button>
      <button onClick={handleLogoutClick} className="settings-modal-logout">
        Logout
      </button>
    </div>
  );

  const renderMainMenu = () => (
    <>
      {renderSidebar("general")}
      <div className="settings-modal-content">
        <h2>General</h2>
        <div className="settings-modal-section">
          <div className="settings-modal-item">
            <span>Theme</span>
            <button>
              <span>{selectedTheme}</span>
              <ChevronRight size={20} />
            </button>
          </div>
          <button className="settings-modal-clickable-item">
            <span className="item-title">Terms and Conditions</span>
            <ChevronRight size={20} />
          </button>
          <button className="settings-modal-clickable-item">
            <span className="item-title">Privacy Policy</span>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </>
  );

  const renderPaymentMenu = () => (
    <>
      {renderSidebar("payment")}
      <div className="settings-modal-content">
        <h2>Payment</h2>
        <div className="settings-modal-section">
          <button onClick={() => setCurrentView("manage-subscription")} className="settings-modal-clickable-item">
            <div className="item-content">
              <div className="item-title">Manage Subscription</div>
              <div className="item-description">View and manage your current plan</div>
            </div>
            <ChevronRight size={20} />
          </button>
          <button onClick={() => setCurrentView("payment-details")} className="settings-modal-clickable-item">
            <div className="item-content">
              <div className="item-title">Payment Details</div>
              <div className="item-description">Update the payment method and billing address on file.</div>
            </div>
            <ChevronRight size={20} />
          </button>
          <button onClick={() => setCurrentView("invoice-details")} className="settings-modal-clickable-item">
            <div className="item-content">
              <div className="item-title">Invoice Details</div>
              <div className="item-description">Update the legal company information on your invoices.</div>
            </div>
            <ChevronRight size={20} />
          </button>
          <button onClick={() => setCurrentView("payment-history")} className="settings-modal-clickable-item">
            <div className="item-content">
              <div className="item-title">Payment History</div>
              <div className="item-description">All your previous payments and invoices.</div>
            </div>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </>
  );

  const renderManageSubscription = () => (
    <>
      {renderSidebar("payment")}
      <div className="settings-modal-content">
        <button onClick={() => setCurrentView("payment")} className="settings-modal-back-button">
          <span>←</span>
          <span>Manage Subscription</span>
        </button>
        <button className="settings-modal-upgrade-button">Upgrade Plan</button>
        <div className="settings-modal-plan-card">
          <div className="plan-header">
            <span className="plan-title">Your Plan</span>
            <span className="plan-type">{userData?.planDetails?.plan_name || "No Plan"}</span>
          </div>
          <div className="plan-features">
            {planDetails?.features?.map((f, idx) => (
              <div key={idx} className="feature-item">
                <Check size={18} />
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="settings-modal-billing-info">
          <div className="billing-row">
            <span className="billing-label">Next Billing</span>
            <span className="billing-value">{userData?.billing_date || "—"}</span>
          </div>
          <div className="billing-row">
            <span className="billing-label">Subtotal</span>
            <span className="billing-value">{planDetails?.priceDisplay || "$0"}</span>
          </div>
          <div className="billing-row">
            <span className="billing-label">Taxes</span>
            <span className="billing-value">$0</span>
          </div>
          <div className="billing-divider"></div>
          <div className="billing-row">
            <span className="billing-label">Total</span>
            <span className="billing-value total">{planDetails?.priceDisplay || "$0"}</span>
          </div>
        </div>
        <button className="settings-modal-cancel-button">Cancel Subscription</button>
      </div>
    </>
  );

  const renderPaymentDetails = () => (
    <>
      {renderSidebar("payment")}
      <div className="settings-modal-content">
        <button onClick={() => setCurrentView("payment")} className="settings-modal-back-button">
          <span>←</span>
          <span>Payment Details</span>
        </button>
        <p className="settings-modal-description">Your payment info is encrypted and kept safe as you update.</p>
        <div className="settings-modal-form">
          <div className="form-group">
            <label>Card Number</label>
            <input type="text" placeholder="XXXX XXXX XXXX XXXX" />
          </div>
          <div className="form-group">
            <label>Name on Card</label>
            <input type="text" placeholder="Card name" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Expire Date</label>
              <input type="text" placeholder="MM / YY" />
            </div>
            <div className="form-group">
              <label>CVV</label>
              <input type="text" placeholder="123" />
            </div>
          </div>
          <button className="submit-button">Update Card Details</button>
        </div>
      </div>
    </>
  );

  const renderInvoiceDetails = () => (
    <>
      {renderSidebar("payment")}
      <div className="settings-modal-content">
        <button onClick={() => setCurrentView("payment")} className="settings-modal-back-button">
          <span>←</span>
          <span>Invoice Details</span>
        </button>
        <p className="settings-modal-description">Keep your billing information up to date to ensure accurate invoices.</p>
        <div className="settings-modal-form">
          <div className="form-group">
            <label>Company Name</label>
            <input type="text" placeholder="Optional" />
          </div>
          <div className="form-group">
            <label>Billing Address</label>
            <input type="text" />
          </div>
          <div className="form-group">
            <label>City</label>
            <input type="text" />
          </div>
          <div className="form-group">
            <label>State / Region</label>
            <input type="text" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>ZIP</label>
              <input type="text" />
            </div>
            <div className="form-group">
              <label>Tax ID / VAT Number</label>
              <input type="text" placeholder="Optional" />
            </div>
          </div>
          <button className="submit-button">Update Details</button>
        </div>
      </div>
    </>
  );

  const renderPaymentHistory = () => (
    <>
      {renderSidebar("payment")}
      <div className="settings-modal-content">
        <button onClick={() => setCurrentView("payment")} className="settings-modal-back-button">
          <span>←</span>
          <span>Payment History</span>
        </button>
        <div className="settings-modal-payment-history">
          {[
            { date: "Aug 27, 2025", amount: "$15,000" },
            { date: "Jun 27, 2025", amount: "$15,000" },
            { date: "Apr 27, 2025", amount: "$15,000" },
          ].map((payment, idx) => (
            <button key={idx} className="payment-item">
              <div className="payment-info">
                <div className="payment-date">{payment.date}</div>
                <div className="payment-amount">{payment.amount}</div>
              </div>
              <div className="payment-actions">
                <span className="payment-status">PAID</span>
                <ChevronRight size={20} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const renderAccountMenu = () => (
    <>
      {renderSidebar("account")}
      <div className="settings-modal-content">
        <h2>Account</h2>

        <div
          className="settings-modal-section"
          style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "center" }}
        >
          <div style={{ position: "relative", marginBottom: "1rem" }}>
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt="Profile"
                style={{ width: "80px", height: "80px", borderRadius: "50%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundImage: "linear-gradient(135deg, #6d8ac4, #002264)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                {initials}
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingPic}
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                background: "none",
                border: "none",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isUploadingPic ? "wait" : "pointer",
                opacity: isUploadingPic ? 0.6 : 1,
              }}
            >
              <CameraAlt style={{ fontSize: "18px", color: "white" }} />
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePicUpload} style={{ display: "none" }} />
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            {isEditingName ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => {
                      const value = e.target.value;
                      const lengthWithoutSpaces = value.replace(/\s/g, "").length;
                      if (lengthWithoutSpaces > NAME_MAX_LENGTH) {
                        setNameError(`Name cannot exceed ${NAME_MAX_LENGTH} characters (excluding spaces)`);
                        return;
                      }
                      setNameError("");
                      setEditedName(value);
                    }}
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: "white",
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid #3b82f6",
                      borderRadius: "4px",
                      padding: "0.5rem",
                      outline: "none",
                      flex: "1",
                      minWidth: "200px",
                    }}
                  />
                  <button
                    onClick={handleNameUpdate}
                    disabled={!!nameError}
                    style={{
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      padding: "0.5rem 1rem",
                      borderRadius: "4px",
                      cursor: nameError ? "not-allowed" : "pointer",
                      opacity: nameError ? 0.6 : 1,
                      fontSize: "0.875rem",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditedName(userData.username); setNameError(""); setIsEditingName(false); }}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.2)",
                      padding: "0.5rem 1rem",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                    }}
                  >
                    Cancel
                  </button>
                </div>
                {nameError && <p style={{ color: "#ef4444", fontSize: "0.75rem" }}>{nameError}</p>}
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "white", margin: 0 }}>{name}</h3>
                <button
                  onClick={() => { setEditedName(userData.username || ""); setNameError(""); setIsEditingName(true); }}
                  style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", opacity: 0.7 }}
                >
                  <Edit2 size={16} color="#9ca3af" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="settings-modal-section">
          <div className="settings-modal-item">
            <span>Role</span>
            <span>{roleName || "—"}</span>
          </div>
          <div className="settings-modal-item">
            <span>Email</span>
            <span>{session?.user?.email}</span>
          </div>

          <button
            onClick={() => setCurrentView("change-password")}
            className="settings-modal-clickable-item"
          >
            <div className="item-content">
              <div className="item-title">Change password</div>
              <div className="item-description">
                It's recommended to change your password periodically for maximum security.
              </div>
            </div>
            <ChevronRight size={20} />
          </button>

          {deactivationError && (
            <div
              style={{
                color: "#ef4444",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                fontSize: "0.875rem",
                marginBottom: "1rem",
                padding: "0.75rem 1rem",
                borderRadius: "8px",
                textAlign: "left",
              }}
            >
              {deactivationError}
            </div>
          )}
          {requestCancelSuccess && (
            <div
              style={{
                color: "#22c55e",
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                border: "1px solid rgba(34, 197, 94, 0.3)",
                fontSize: "0.875rem",
                marginBottom: "1rem",
                padding: "0.75rem 1rem",
                borderRadius: "8px",
                textAlign: "left",
              }}
            >
              Request sent successfully. An admin will be notified.
            </div>
          )}
          {requestCancelError && (
            <div
              style={{
                color: "#ef4444",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                fontSize: "0.875rem",
                marginBottom: "1rem",
                padding: "0.75rem 1rem",
                borderRadius: "8px",
                textAlign: "left",
              }}
            >
              {requestCancelError}
            </div>
          )}

          {/* Action buttons row */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "1rem" }}>
            {isOrgDeactivated?.toUpperCase() === "PENDING" && (
              <button
                onClick={handleRequestCancelDeactivation}
                disabled={isRequestingCancelDeactivation || requestCancelSuccess}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#22c55e",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  cursor: isRequestingCancelDeactivation || requestCancelSuccess ? "not-allowed" : "pointer",
                  opacity: isRequestingCancelDeactivation || requestCancelSuccess ? 0.7 : 1,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => { if (!isRequestingCancelDeactivation && !requestCancelSuccess) e.target.style.backgroundColor = "#16a34a"; }}
                onMouseLeave={(e) => { if (!isRequestingCancelDeactivation && !requestCancelSuccess) e.target.style.backgroundColor = "#22c55e"; }}
              >
                {isRequestingCancelDeactivation ? "Sending..." : requestCancelSuccess ? "Request Sent ✓" : "Request Role Change"}
              </button>
            )}

            {/* Deactivate Account */}
            <button
              onClick={() => setShowDeactivateDialog(true)}
              style={{
                padding: "12px 24px",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "0.875rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#dc2626")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#ef4444")}
            >
              {isDeactivating ? "Deactivating..." : "Deactivate Account"}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const renderChangePassword = () => (
    <>
      {renderSidebar("account")}
      <div className="settings-modal-content">
        <button onClick={() => setCurrentView("account")} className="settings-modal-back-button">
          <span>←</span>
          <span>Change Password</span>
        </button>
        {passwordSuccess && (
          <div className="password-success">
            <Check size={18} />
            <span>Password updated successfully</span>
          </div>
        )}
        <div className="settings-modal-form">
          <div className="form-group">
            <label>Current Password</label>
            <div className="password-wrapper">
              <input
                type={showOldPassword ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => { setOldPassword(e.target.value); setErrors((prev) => ({ ...prev, oldPassword: null })); }}
                placeholder="Enter current password"
              />
              <span className="toggle-eye" onClick={() => setShowOldPassword((p) => !p)}>
                {showOldPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>
            {errors.oldPassword && <p className="form-error">{errors.oldPassword}</p>}
          </div>

          <div className="form-group">
            <label>New Password</label>
            <div className="password-wrapper">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setErrors((prev) => ({ ...prev, newPassword: null })); }}
                placeholder="Enter new password"
              />
              <span className="toggle-eye" onClick={() => setShowNewPassword((p) => !p)}>
                {showNewPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>
            {errors.newPassword && <p className="form-error">{errors.newPassword}</p>}
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <div className="password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setErrors((prev) => ({ ...prev, confirmPassword: null })); }}
                placeholder="Re-enter new password"
              />
              <span className="toggle-eye" onClick={() => setShowConfirmPassword((p) => !p)}>
                {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>
            {errors.confirmPassword && <p className="form-error">{errors.confirmPassword}</p>}
          </div>

          <button
            className="submit-button"
            onClick={handleChangePassword}
            disabled={isUpdatingPassword || passwordSuccess}
          >
            {isUpdatingPassword ? "Updating..." : "Change Password"}
          </button>
        </div>
      </div>
    </>
  );

  const renderView = () => {
    switch (currentView) {
      case "payment": return renderPaymentMenu();
      case "manage-subscription": return renderManageSubscription();
      case "payment-details": return renderPaymentDetails();
      case "invoice-details": return renderInvoiceDetails();
      case "payment-history": return renderPaymentHistory();
      case "account": return renderAccountMenu();
      case "change-password": return renderChangePassword();
      default: return renderMainMenu();
    }
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal-container" onClick={(e) => e.stopPropagation()}>
        {renderView()}
        <Dialog
          open={showDeactivateDialog}
          onClose={() => {}}
          sx={{
            zIndex: 9999,
            "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)", zIndex: 9998 },
          }}
          PaperProps={{
            sx: {
              backgroundColor: "#000000",
              border: "2px solid #ef4444",
              borderRadius: "8px",
              color: "white",
              width: "450px",
              maxWidth: "90vw",
              zIndex: 9999,
            },
          }}
        >
          <DialogTitle sx={{ backgroundColor: "#000000", color: "white", textAlign: "center", fontSize: "1.3rem", fontWeight: "bold", paddingBottom: "10px" }}>
            Deactivate Account?
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: "#000000", color: "white", textAlign: "center", padding: "20px" }}>
            <Typography sx={{ fontSize: "1rem", mb: 2 }}>
              Are you sure you want to deactivate your account? This will take effect immediately and you will be logged out.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: "#000000", justifyContent: "center", paddingBottom: "20px", gap: "12px" }}>
            <Button
              onClick={() => setShowDeactivateDialog(false)}
              sx={{ color: "#666", border: "1px solid #444", padding: "10px 30px", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", borderColor: "#666" } }}
            >
              CANCEL
            </Button>
            <Button
              onClick={handleDeactivateAccount}
              variant="contained"
              disabled={isDeactivating}
              sx={{ backgroundColor: "#ef4444", color: "white", padding: "10px 30px", "&:hover": { backgroundColor: "#dc2626" } }}
            >
              {isDeactivating ? "DEACTIVATING..." : "DEACTIVATE"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsModal;